import { useEffect, useState, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { useDispatch } from "react-redux";
import { traderApi } from "../store/trader-api";

interface UseTraderNotificationsReturn {
  isConnected: boolean;
  error: string | null;
}

export const useTraderNotifications = (
  traderId: string | undefined,
  backendUrl: string = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:3001"
): UseTraderNotificationsReturn => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Invalidate RTK Query cache to refresh data
  const invalidateCounters = useCallback(() => {
    // Invalidate both orders and appeals queries to get fresh counts
    dispatch(traderApi.util.invalidateTags(["Order", "Appeal"]));
  }, [dispatch]);

  useEffect(() => {
    if (!traderId) {
      return;
    }

    // Create WebSocket connection to trader-notifications namespace
    const socket = io(`${backendUrl}/trader-notifications`, {
      transports: ["websocket", "polling"],
      timeout: 20000,
      autoConnect: true,
      auth: {
        traderId,
      },
      query: {
        traderId,
      },
    });

    socketRef.current = socket;

    // Connection handlers
    socket.on("connect", () => {
      console.log("🔗 WebSocket connected to trader notifications");
      setIsConnected(true);
      setError(null);
    });

    socket.on("disconnect", () => {
      console.log("🔌 WebSocket disconnected from trader notifications");
      setIsConnected(false);
    });

    socket.on("connect_error", (err) => {
      console.error("❌ WebSocket connection error (trader notifications):", err);
      setError(err.message);
      setIsConnected(false);
    });

    socket.on("connected", (data) => {
      console.log("✅ Trader notifications WebSocket handshake:", data);
    });

    // Event: Full counters update (contains both values)
    socket.on("counters-update", (data: { pendingOrders: number; pendingAppeals: number }) => {
      console.log("📊 Counters update received:", data);
      invalidateCounters();
    });

    // Event: New pending order added
    socket.on("new-pending-order", (data: { orderId: string }) => {
      console.log("📦 New pending order:", data.orderId);
      invalidateCounters();
    });

    // Event: New pending appeal added
    socket.on("new-pending-appeal", (data: { appealId: string }) => {
      console.log("⚠️ New pending appeal:", data.appealId);
      invalidateCounters();
    });

    // Event: Counter changed (generic - used for decrements or any change)
    socket.on("counter-change", (data: { type: "order" | "appeal"; change: "added" | "removed" }) => {
      console.log("🔄 Counter change:", data);
      invalidateCounters();
    });

    // Cleanup on unmount
    return () => {
      console.log("🧹 Cleaning up trader notifications WebSocket connection");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [backendUrl, traderId, invalidateCounters]);

  return {
    isConnected,
    error,
  };
};
